import { Button, Divider, IconButton, Typography } from "@suraasa/placebo-ui"
import { Assignment } from "api/resources/assignments/types"
import { Attachment, NavArrowLeft, NavArrowRight } from "iconoir-react"
import { useEffect, useRef, useState } from "react"

import { formatAssignmentTitle } from "../utils"
import FAQsSheet from "./FAQsSheet"
import Instructions from "./Instructions"
import LearningOutcomesSheet from "./LearningOutcomesSheet"
import SubmissionGuidelines from "./SubmissionGuidelines"
import { hasContent, isBlank } from "./utils"

type SheetType =
  | "instructions"
  | "guidelines"
  | "faqs"
  | "learningOutcomes"
  | null

// Legacy descriptions stored raw newlines with no block tags around them. Newer ones are
// real HTML that is often pretty-printed, so replacing newlines there would add spurious
// breaks between blocks.
const formatDescription = (description: string | null | undefined) => {
  const value = description ?? ""
  if (/<(p|div|ul|ol|br|h[1-6]|table)\b/i.test(value)) return value
  return value.replace(/\r\n|\r|\n/g, "<br />")
}

const TaskDescription = ({
  assignment,
  richTextClassName,
}: {
  assignment: Assignment
  richTextClassName: string
}) => {
  const [openSheet, setOpenSheet] = useState<SheetType>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const [showLeft, setShowLeft] = useState(false)
  const [showRight, setShowRight] = useState(false)

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    const update = () => {
      setShowLeft(el.scrollLeft > 0)
      setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
    }

    update()
    el.addEventListener("scroll", update)
    window.addEventListener("resize", update)

    return () => {
      el.removeEventListener("scroll", update)
      window.removeEventListener("resize", update)
    }
  }, [])

  const formattedAssignmentDescription = formatDescription(
    assignment.description
  )

  const visibleFaqs = (assignment.faqs ?? []).filter(
    faq => !isBlank(faq.question) && !isBlank(faq.answer)
  )
  const hasFaqs = visibleFaqs.length > 0

  const visibleAttachments = (assignment.attachments ?? []).filter(
    file => !isBlank(file.file) && !isBlank(file.name)
  )

  const { instructions, submissionGuidelines } = assignment
  const hasInstructions = hasContent(instructions)
  const hasSubmissionGuidelines = hasContent(submissionGuidelines)
  const hasAssessmentCriteria = Boolean(
    assignment.assignmentAssessmentCriteria?.length
  )
  const hasPedagogicalSkillEvaluated = !isBlank(
    assignment.pedagogicalSkillEvaluated
  )
  const hasLearningOutcomes =
    hasAssessmentCriteria || hasPedagogicalSkillEvaluated

  return (
    <div className="flex flex-col gap-1.5 rounded-xl border border-onSurface-300 p-2">
      <Typography variant="title4">
        {formatAssignmentTitle(assignment)}
      </Typography>

      <div
        className={richTextClassName}
        dangerouslySetInnerHTML={{
          __html: formattedAssignmentDescription,
        }}
      />

      <div className="relative mt-2">
        <div className="relative">
          {showLeft && (
            <div className="pointer-events-none absolute inset-y-0 left-0 w-5 bg-gradient-to-r from-surface to-transparent" />
          )}

          {showRight && (
            <div className="pointer-events-none absolute inset-y-0 right-0 w-5 bg-gradient-to-l from-surface to-transparent" />
          )}
          <div
            ref={scrollRef}
            className="scrollbar-hide mb-2 flex items-center gap-1 overflow-x-auto whitespace-nowrap"
          >
            {hasSubmissionGuidelines && (
              <Button
                size="sm"
                variant="filled"
                color="black"
                className="shrink-0 whitespace-nowrap rounded-[10px] px-1 py-[7px]"
                onClick={() => setOpenSheet("guidelines")}
              >
                Submission Guidelines
              </Button>
            )}

            {hasInstructions && (
              <Button
                size="sm"
                variant="filled"
                color="black"
                className="shrink-0 whitespace-nowrap rounded-[10px] px-1 py-[7px]"
                onClick={() => setOpenSheet("instructions")}
              >
                Task Instructions
              </Button>
            )}

            {hasLearningOutcomes && (
              <Button
                size="sm"
                variant="outlined"
                color="black"
                className="shrink-0 whitespace-nowrap rounded-[10px] border border-onSurface-300 px-1 py-[7px]"
                onClick={() => setOpenSheet("learningOutcomes")}
              >
                Learning Outcomes
              </Button>
            )}

            {hasFaqs && (
              <Button
                size="sm"
                variant="outlined"
                color="black"
                className="shrink-0 whitespace-nowrap rounded-[10px] border border-onSurface-300 px-1 py-[7px]"
                onClick={() => setOpenSheet("faqs")}
              >
                FAQs
              </Button>
            )}
          </div>
        </div>

        {(showLeft || showRight) && (
          <div className="mr-1 flex flex-row items-center justify-end gap-2">
            <IconButton
              size="sm"
              onClick={() =>
                scrollRef.current?.scrollBy({
                  left: -150,
                  behavior: "smooth",
                })
              }
            >
              <NavArrowLeft className="text-onSurface-900" />
            </IconButton>
            <IconButton
              size="sm"
              onClick={() =>
                scrollRef.current?.scrollBy({ left: 150, behavior: "smooth" })
              }
            >
              <NavArrowRight className="text-onSurface-900" />
            </IconButton>
          </div>
        )}
      </div>

      {visibleAttachments.length > 0 && (
        <div>
          <Divider weight="light" className="my-1 bg-onSurface-200" />
          <div className="flex flex-wrap items-center gap-1">
            {visibleAttachments.map((file, index) => (
              <a
                key={index}
                href={file.file}
                target="_blank"
                rel="noreferrer"
                className="flex"
              >
                <div className="flex items-center gap-1">
                  <div className="rounded border p-1">
                    <Attachment className="size-2.5" />
                  </div>
                  <Typography variant="strongSmallBody">{file.name}</Typography>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}

      {hasInstructions && (
        <Instructions
          instructions={instructions}
          richTextClassName={richTextClassName}
          open={openSheet === "instructions"}
          onClose={() => setOpenSheet(null)}
        />
      )}

      {hasSubmissionGuidelines && (
        <SubmissionGuidelines
          guidelines={submissionGuidelines}
          richTextClassName={richTextClassName}
          open={openSheet === "guidelines"}
          onClose={() => setOpenSheet(null)}
        />
      )}

      {hasLearningOutcomes && (
        <LearningOutcomesSheet
          open={openSheet === "learningOutcomes"}
          onClose={() => setOpenSheet(null)}
          pedagogicalSkillEvaluated={assignment.pedagogicalSkillEvaluated}
          acs={assignment.assignmentAssessmentCriteria ?? []}
        />
      )}

      {hasFaqs && (
        <FAQsSheet
          faqs={visibleFaqs}
          open={openSheet === "faqs"}
          onClose={() => setOpenSheet(null)}
        />
      )}
    </div>
  )
}

export default TaskDescription
